// src/services/coupleService.js
// Updated with Pre-Marriage Intensive permissions support
// FIXED: verifyCounselorCode now uses document ID instead of userId field

import { collection, query, where, getDocs, doc, setDoc, updateDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Verify counselor code exists
 * FIXED: Now returns snapshot.docs[0].id as counselorId instead of counselorData.userId
 */
export const verifyCounselorCode = async (code) => {
  try {
    const counselorsQuery = query(
      collection(db, 'users'),
      where('role', '==', 'counselor'),
      where('counselorCode', '==', code.toUpperCase())
    );
    
    const snapshot = await getDocs(counselorsQuery);
    
    if (snapshot.empty) {
      return { 
        valid: false, 
        counselorId: null, 
        counselorName: null,
        error: 'Invalid counselor code' 
      };
    }
    
    const counselorDoc = snapshot.docs[0];
    const counselorData = counselorDoc.data();
    
    // FIX: Use the document ID as counselorId, not counselorData.userId
    // The document ID IS the counselor's Firebase Auth UID
    return {
      valid: true,
      counselorId: counselorDoc.id,  // <-- THIS IS THE FIX
      counselorName: counselorData.name,
      counselorEmail: counselorData.email,
      error: null
    };
  } catch (error) {
    console.error('Error verifying counselor code:', error);
    return { 
      valid: false, 
      counselorId: null, 
      counselorName: null,
      error: error.message 
    };
  }
};

/**
 * Generate unique couple code
 * Format: COUNSELORCODE-CXXX (e.g., ABC123-C001)
 */
export const generateCoupleCode = async (counselorCode) => {
  try {
    console.log('Generating couple code for counselor:', counselorCode);
    
    // Count existing couples for this counselor
    const couplesQuery = query(
      collection(db, 'couples'),
      where('counselorCode', '==', counselorCode.toUpperCase())
    );
    
    const couplesSnapshot = await getDocs(couplesQuery);
    const coupleCount = couplesSnapshot.size + 1;
    
    // Format: ABC123-C001, ABC123-C002, etc.
    const coupleCode = `${counselorCode.toUpperCase()}-C${String(coupleCount).padStart(3, '0')}`;
    
    console.log('Generated couple code:', coupleCode);
    
    return coupleCode;
    
  } catch (error) {
    console.error('Error generating couple code:', error);
    throw error;
  }
};

/**
 * Initialize factor permissions (20 factors - all locked)
 */
const initializeFactorPermissions = () => {
  const permissions = {};
  for (let i = 1; i <= 20; i++) {
    permissions[i] = {
      canTake: false,
      resultsRevealed: false,
      unlockedAt: null,
      revealedAt: null,
      revealedBy: null
    };
  }
  return permissions;
};

/**
 * Initialize intensive module permissions (10 modules - all locked)
 */
const initializeIntensivePermissions = () => {
  const permissions = {};
  for (let i = 1; i <= 10; i++) {
    permissions[i] = {
      canTake: false,
      resultsRevealed: false,
      unlockedAt: null,
      revealedAt: null,
      revealedBy: null
    };
  }
  return permissions;
};

/**
 * Create couple document linking both partners
 * Now includes both factorPermissions (20) and intensivePermissions (10)
 */
export const createCouple = async (
  partner1Id,
  partner2Id,
  partner1Name,
  partner2Name,
  counselorId,
  counselorCode
) => {
  try {
    console.log('coupleService: Creating couple');
    
    // Get counselor info
    const counselorDocRef = doc(db, 'users', counselorId);
    const counselorDoc = await getDoc(counselorDocRef);
    
    if (!counselorDoc.exists()) {
      throw new Error('Counselor not found');
    }
    
    const counselorData = counselorDoc.data();
    
    // Get partner emails
    const partner1DocRef = doc(db, 'users', partner1Id);
    const partner2DocRef = doc(db, 'users', partner2Id);
    
    const partner1Doc = await getDoc(partner1DocRef);
    const partner2Doc = await getDoc(partner2DocRef);
    
    const partner1Email = partner1Doc.data()?.email || '';
    const partner2Email = partner2Doc.data()?.email || '';
    
    // Generate couple ID
    const coupleId = `couple_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Initialize permissions for both systems
    const factorPermissions = initializeFactorPermissions();         // 20 factors
    const intensivePermissions = initializeIntensivePermissions();   // 10 modules
    
    // Create couple document
    await setDoc(doc(db, 'couples', coupleId), {
      id: coupleId,
      coupleId: coupleId,
      partner1Id,
      partner1Name,
      partner1Email,
      partner2Id,
      partner2Name,
      partner2Email,
      counselorId,
      counselorName: counselorData.name,
      counselorEmail: counselorData.email,
      counselorCode: counselorCode.toUpperCase(),
      approvalStatus: 'pending',
      approvedAt: null,
      rejectedAt: null,
      rejectionReason: null,
      sharingEnabled: false,
      factorPermissions,           // 20-factor assessment permissions
      intensivePermissions,        // 10-module intensive permissions
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update Partner 1's profile
    await updateDoc(doc(db, 'users', partner1Id), {
      coupleId,
      partnerId: partner2Id,
      partnerName: partner2Name,
      approvalStatus: 'pending'
    });
    
    // Update Partner 2's profile
    await updateDoc(doc(db, 'users', partner2Id), {
      coupleId,
      partnerId: partner1Id,
      partnerName: partner1Name,
      approvalStatus: 'pending'
    });
    
    console.log('coupleService: Couple created successfully:', coupleId);
    
    return {
      success: true,
      coupleId,
      error: null
    };
    
  } catch (error) {
    console.error('coupleService: Error creating couple:', error);
    return {
      success: false,
      coupleId: null,
      error: error.message
    };
  }
};

/**
 * Get all couples for a specific counselor
 */
export const getAllCouples = async (counselorId) => {
  try {
    console.log('coupleService: Getting all couples for counselor:', counselorId);
    
    const couplesQuery = query(
      collection(db, 'couples'),
      where('counselorId', '==', counselorId)
    );
    
    const snapshot = await getDocs(couplesQuery);
    
    const couples = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('coupleService: Found couples:', couples.length);
    
    return couples;
    
  } catch (error) {
    console.error('coupleService: Error getting couples:', error);
    return [];
  }
};

/**
 * Get pending couples for a counselor
 */
export const getPendingCouples = async (counselorId) => {
  try {
    console.log('coupleService: Getting pending couples for counselor:', counselorId);
    
    const couplesQuery = query(
      collection(db, 'couples'),
      where('counselorId', '==', counselorId),
      where('approvalStatus', '==', 'pending')
    );
    
    const snapshot = await getDocs(couplesQuery);
    
    const couples = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('coupleService: Found pending couples:', couples.length);
    
    return couples;
    
  } catch (error) {
    console.error('coupleService: Error getting pending couples:', error);
    return [];
  }
};

/**
 * Get approved couples for a counselor
 */
export const getApprovedCouples = async (counselorId) => {
  try {
    console.log('coupleService: Getting approved couples for counselor:', counselorId);
    
    const couplesQuery = query(
      collection(db, 'couples'),
      where('counselorId', '==', counselorId),
      where('approvalStatus', '==', 'approved')
    );
    
    const snapshot = await getDocs(couplesQuery);
    
    const couples = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('coupleService: Found approved couples:', couples.length);
    
    return couples;
    
  } catch (error) {
    console.error('coupleService: Error getting approved couples:', error);
    return [];
  }
};

/**
 * Approve a couple
 * Unlocks Factor 1 and Module 1 automatically
 */
export const approveCouple = async (coupleId) => {
  try {
    console.log('coupleService: Approving couple:', coupleId);
    
    const coupleRef = doc(db, 'couples', coupleId);
    const coupleDoc = await getDoc(coupleRef);
    
    if (!coupleDoc.exists()) {
      throw new Error('Couple not found');
    }
    
    const coupleData = coupleDoc.data();
    
    // Update couple document - unlock Factor 1 AND Module 1
    await updateDoc(coupleRef, {
      approvalStatus: 'approved',
      approvedAt: serverTimestamp(),
      rejectedAt: null,
      rejectionReason: null,
      // Unlock Factor 1 (20-factor assessment)
      'factorPermissions.1.canTake': true,
      'factorPermissions.1.unlockedAt': serverTimestamp(),
      // Unlock Module 1 (Pre-Marriage Intensive)
      'intensivePermissions.1.canTake': true,
      'intensivePermissions.1.unlockedAt': serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update both partners' status
    await updateDoc(doc(db, 'users', coupleData.partner1Id), {
      approvalStatus: 'approved'
    });
    
    await updateDoc(doc(db, 'users', coupleData.partner2Id), {
      approvalStatus: 'approved'
    });
    
    console.log('coupleService: Couple approved successfully');
    
    return { success: true, error: null };
    
  } catch (error) {
    console.error('coupleService: Error approving couple:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reject a couple
 */
export const rejectCouple = async (coupleId, reason = '') => {
  try {
    console.log('coupleService: Rejecting couple:', coupleId);
    
    const coupleRef = doc(db, 'couples', coupleId);
    const coupleDoc = await getDoc(coupleRef);
    
    if (!coupleDoc.exists()) {
      throw new Error('Couple not found');
    }
    
    const coupleData = coupleDoc.data();
    
    // Update couple document
    await updateDoc(coupleRef, {
      approvalStatus: 'rejected',
      rejectedAt: serverTimestamp(),
      rejectionReason: reason || null,
      approvedAt: null,
      updatedAt: serverTimestamp()
    });
    
    // Update both partners' status
    await updateDoc(doc(db, 'users', coupleData.partner1Id), {
      approvalStatus: 'rejected'
    });
    
    await updateDoc(doc(db, 'users', coupleData.partner2Id), {
      approvalStatus: 'rejected'
    });
    
    console.log('coupleService: Couple rejected');
    
    return { success: true, error: null };
    
  } catch (error) {
    console.error('coupleService: Error rejecting couple:', error);
    return { success: false, error: error.message };
  }
};

/**
 * DELETE a couple and all associated data
 * Removes: couple document, both user profiles, both assessments
 */
export const deleteCouple = async (coupleId) => {
  try {
    console.log('coupleService: Deleting couple:', coupleId);
    
    const coupleRef = doc(db, 'couples', coupleId);
    const coupleDoc = await getDoc(coupleRef);
    
    if (!coupleDoc.exists()) {
      throw new Error('Couple not found');
    }
    
    const coupleData = coupleDoc.data();
    const { partner1Id, partner2Id } = coupleData;
    
    console.log('Deleting data for partners:', partner1Id, partner2Id);
    
    // Delete Partner 1's assessment
    if (partner1Id) {
      try {
        const assessment1Ref = doc(db, 'assessments', partner1Id);
        const assessment1Doc = await getDoc(assessment1Ref);
        if (assessment1Doc.exists()) {
          await deleteDoc(assessment1Ref);
          console.log('✅ Deleted Partner 1 assessment');
        }
      } catch (e) {
        console.log('Partner 1 assessment not found');
      }
      
      // Delete Partner 1's user profile
      try {
        const user1Ref = doc(db, 'users', partner1Id);
        const user1Doc = await getDoc(user1Ref);
        if (user1Doc.exists()) {
          await deleteDoc(user1Ref);
          console.log('✅ Deleted Partner 1 user profile');
        }
      } catch (e) {
        console.log('Partner 1 user profile not found');
      }
    }
    
    // Delete Partner 2's assessment
    if (partner2Id) {
      try {
        const assessment2Ref = doc(db, 'assessments', partner2Id);
        const assessment2Doc = await getDoc(assessment2Ref);
        if (assessment2Doc.exists()) {
          await deleteDoc(assessment2Ref);
          console.log('✅ Deleted Partner 2 assessment');
        }
      } catch (e) {
        console.log('Partner 2 assessment not found');
      }
      
      // Delete Partner 2's user profile
      try {
        const user2Ref = doc(db, 'users', partner2Id);
        const user2Doc = await getDoc(user2Ref);
        if (user2Doc.exists()) {
          await deleteDoc(user2Ref);
          console.log('✅ Deleted Partner 2 user profile');
        }
      } catch (e) {
        console.log('Partner 2 user profile not found');
      }
    }
    
    // Delete the couple document
    await deleteDoc(coupleRef);
    console.log('✅ Deleted couple document');
    
    console.log('coupleService: Couple deleted successfully');
    
    return { success: true, error: null };
    
  } catch (error) {
    console.error('coupleService: Error deleting couple:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Toggle result sharing between partners
 */
export const toggleSharing = async (coupleId, enabled) => {
  try {
    console.log('coupleService: Toggling sharing for couple:', coupleId, 'to:', enabled);
    
    const coupleRef = doc(db, 'couples', coupleId);
    
    await updateDoc(coupleRef, {
      sharingEnabled: enabled,
      updatedAt: serverTimestamp()
    });
    
    console.log('coupleService: Sharing toggled successfully');
    
    return { success: true, error: null };
    
  } catch (error) {
    console.error('coupleService: Error toggling sharing:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a specific couple by ID
 */
export const getCouple = async (coupleId) => {
  try {
    console.log('coupleService: Getting couple:', coupleId);
    
    const coupleRef = doc(db, 'couples', coupleId);
    const coupleDoc = await getDoc(coupleRef);
    
    if (!coupleDoc.exists()) {
      console.log('coupleService: Couple not found');
      return null;
    }
    
    const coupleData = {
      id: coupleDoc.id,
      ...coupleDoc.data()
    };
    
    console.log('coupleService: Couple found:', coupleData.partner1Name, '&', coupleData.partner2Name);
    
    return coupleData;
    
  } catch (error) {
    console.error('coupleService: Error getting couple:', error);
    return null;
  }
};

/**
 * Add intensive permissions to existing couple (migration helper)
 * Use this for couples created before the intensive feature was added
 */
export const addIntensivePermissionsToCouple = async (coupleId) => {
  try {
    console.log('coupleService: Adding intensive permissions to couple:', coupleId);
    
    const coupleRef = doc(db, 'couples', coupleId);
    const coupleDoc = await getDoc(coupleRef);
    
    if (!coupleDoc.exists()) {
      throw new Error('Couple not found');
    }
    
    const coupleData = coupleDoc.data();
    
    // Check if already has intensive permissions
    if (coupleData.intensivePermissions) {
      console.log('coupleService: Couple already has intensive permissions');
      return { success: true, error: null };
    }
    
    // Initialize intensive permissions
    const intensivePermissions = initializeIntensivePermissions();
    
    // If couple is approved, unlock Module 1
    if (coupleData.approvalStatus === 'approved') {
      intensivePermissions[1].canTake = true;
    }
    
    await updateDoc(coupleRef, {
      intensivePermissions,
      updatedAt: serverTimestamp()
    });
    
    console.log('coupleService: Intensive permissions added successfully');
    
    return { success: true, error: null };
    
  } catch (error) {
    console.error('coupleService: Error adding intensive permissions:', error);
    return { success: false, error: error.message };
  }
};
